import React, { memo, useState, useRef, useEffect, useCallback } from 'react'
import { createPortal } from 'react-dom'
import { AnimatePresence } from 'framer-motion'
import { useResponsive } from 'hooks/useResponsive'
import { useOnClickOutside } from 'hooks/useOnClickOutside'
import { SMALL_DESKTOP_SIZE_BREAKPOINT } from 'consts'
import { DEFAULT_COORDS, TOP } from './consts'
import { getCoordsAndPosition } from './utils'
import * as Styled from './styled'
import type { IProps } from './types'

const MOBILE_AUTO_CLOSE_MS = 3000

export const Tooltip = React.forwardRef<HTMLElement, IProps>(
  (
    {
      children,
      tooltipText,
      position: preferredPosition = TOP,
      isVisible,
      showOnMobileTap = false,
      ...rest
    },
    forwardedRef
  ) => {
    const [internalIsVisible, setInternalIsVisible] = useState(false)
    const [coords, setCoords] = useState(DEFAULT_COORDS)
    const [position, setPosition] = useState(preferredPosition)
    const mobileCloseTimerRef = useRef<ReturnType<typeof setTimeout> | null>(
      null
    )

    const wrapperRef = useRef<HTMLDivElement | null>(null)
    const tooltipRef = useRef<HTMLDivElement | null>(null)

    const isMobile = useResponsive(SMALL_DESKTOP_SIZE_BREAKPOINT)

    // isVisible=true: enable hover, isVisible=false: disable completely, undefined: normal hover
    const canShowTooltip = isVisible !== false
    const tooltipIsVisible = canShowTooltip && internalIsVisible

    const clearMobileTimer = () => {
      if (mobileCloseTimerRef.current) {
        clearTimeout(mobileCloseTimerRef.current)
        mobileCloseTimerRef.current = null
      }
    }

    const showTooltip = () => {
      if (!canShowTooltip) return
      setInternalIsVisible(true)
    }

    const hideTooltip = () => {
      if (!canShowTooltip) return
      setInternalIsVisible(false)
    }

    const handleMobileTap = useCallback(() => {
      if (!canShowTooltip) return
      setInternalIsVisible(wasVisible => {
        const shouldBeVisible = !wasVisible
        clearMobileTimer()
        if (shouldBeVisible) {
          mobileCloseTimerRef.current = setTimeout(
            () => setInternalIsVisible(false),
            MOBILE_AUTO_CLOSE_MS
          )
        }
        return shouldBeVisible
      })
    }, [canShowTooltip])

    const mobileTapEnabled = isMobile && showOnMobileTap

    useOnClickOutside(
      mobileTapEnabled && tooltipIsVisible ? [wrapperRef.current] : null,
      () => {
        clearMobileTimer()
        setInternalIsVisible(false)
      }
    )

    useEffect(() => () => clearMobileTimer(), [])

    useEffect(() => {
      if (!tooltipIsVisible) return

      const { x, y, position } = getCoordsAndPosition(
        tooltipRef,
        wrapperRef,
        preferredPosition
      )

      setCoords({ x, y })
      setPosition(position)
    }, [tooltipIsVisible, tooltipRef, wrapperRef, preferredPosition])

    const childOnClick = children.props.onClick as
      | ((e: React.MouseEvent) => void)
      | undefined

    const handleMobileWrapperClick = (e: React.MouseEvent) => {
      childOnClick?.(e)
      handleMobileTap()
    }

    return (
      <>
        {React.cloneElement(children, {
          onMouseEnter: isMobile ? undefined : showTooltip,
          onMouseLeave: isMobile ? undefined : hideTooltip,
          ...(mobileTapEnabled && { onClick: handleMobileWrapperClick }),
          ref: (node: HTMLDivElement) => {
            // Handle both the forwarded ref and the internal wrapperRef
            if (typeof forwardedRef === 'function') {
              forwardedRef(node)
            } else if (forwardedRef) {
              forwardedRef.current = node
            }
            wrapperRef.current = node
          }
        })}
        {tooltipIsVisible && !!tooltipText
          ? createPortal(
              <AnimatePresence>
                <Styled.Tooltip
                  ref={tooltipRef}
                  position={position}
                  coords={coords}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  {...rest}
                >
                  {tooltipText}
                  <div className="triangle" />
                </Styled.Tooltip>
              </AnimatePresence>,
              document.body
            )
          : null}
      </>
    )
  }
)

Tooltip.displayName = 'Tooltip'

export default memo(Tooltip)
